import { type Dispatch } from '@reduxjs/toolkit'
import i18n from 'i18next'

import { pushErrorNotification, pushSuccessNotification } from '../store/notifications/actions'
import { type NotificationAction } from '../store/notifications/types'
import { type PostResponse } from '../types'

const NEW_POST_PATH = '/posts/new'

// The container hides a notification after 5000ms by default, which is short for
// a toast the user is meant to act on.
const PUBLISHED_NOTIFICATION_DELAY_MS = 12000

const MESSAGE_KEYS = {
  SENT: {
    detailed: 'notifications.post-published',
    generic: 'notifications.post-published-generic',
  },
  FAILED: { detailed: 'notifications.post-failed', generic: 'notifications.post-failed-generic' },
} as const

const buildMessage = (status: 'SENT' | 'FAILED', postResponse: PostResponse): string => {
  const platform = postResponse.socialAccount?.platform
  const postType = postResponse.postType
  const keys = MESSAGE_KEYS[status]

  if (!platform || !postType) {
    return i18n.t(keys.generic)
  }

  return i18n.t(keys.detailed, { platform, postType })
}

const buildActions = (postResponse: PostResponse): NotificationAction[] => {
  const postLink = postResponse.postLink

  return [
    ...(postLink ? [{ label: i18n.t('posts.preview.visit-post'), href: postLink }] : []),
    { label: i18n.t('common.new-post'), path: NEW_POST_PATH },
  ]
}

export function sendPostNotification(dispatch: Dispatch, postResponse: PostResponse): void {
  const status = postResponse.status

  if (status !== 'SENT' && status !== 'FAILED') {
    return
  }

  const message = buildMessage(status, postResponse)

  if (status === 'SENT') {
    dispatch(
      pushSuccessNotification({
        message,
        extra: {
          actions: buildActions(postResponse),
          delay: PUBLISHED_NOTIFICATION_DELAY_MS,
        },
      }),
    )
    return
  }

  dispatch(pushErrorNotification({ message }))
}
